import { z } from 'zod';

export const SourceChannelKind = z.enum(['EVENTS', 'NEWS']);
export type SourceChannelKind = z.infer<typeof SourceChannelKind>;

// Normalizes telegram channel slug: strips leading `@`, trims, lowercases.
const channelSlug = z.preprocess(
  (v) => {
    if (typeof v !== 'string') return v;
    const trimmed = v.trim();
    const noAt = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
    return noAt.toLowerCase();
  },
  z
    .string()
    .min(5)
    .max(32)
    .regex(/^[a-z0-9_]+$/, 'invalid telegram channel slug'),
);

const TME_REF_RE = /^(?:https?:\/\/)?(?:www\.)?t\.me\/(?:s\/)?([A-Za-z0-9_]+)(?:\/(\d+))?/;

/**
 * Разбирает адрес источника, введённый админом.
 *
 * Принимает `slug`, `@slug`, `t.me/slug`, `https://t.me/s/slug` и ссылку на
 * тему форума `https://t.me/slug/8270` (а также ссылку на сообщение внутри
 * темы — `.../8270/54630`, тема берётся из первого числового сегмента).
 *
 * `topicId = 0` — обычный канал целиком. Отличить ссылку на тему форума от
 * ссылки на пост обычного канала по URL нельзя: и то и другое выглядит как
 * `t.me/<slug>/<число>`. Ошибку ловит уже проверка источника на t.me.
 */
export function parseSourceChannelRef(input: string): { slug: string; topicId: number } | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;

  const url = TME_REF_RE.exec(trimmed);
  if (url?.[1]) {
    return { slug: url[1].toLowerCase(), topicId: url[2] ? Number.parseInt(url[2], 10) : 0 };
  }

  const bare = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
  return { slug: bare.toLowerCase(), topicId: 0 };
}

/**
 * Стартовое сообщение режима id-walk: голое число либо ссылка на сообщение
 * (`https://t.me/<slug>/31842`). `null` — ни то ни другое.
 */
export function parseTmeMessageId(input: unknown): number | null {
  if (typeof input === 'number') return Number.isInteger(input) ? input : null;
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (/^\d+$/.test(trimmed)) return Number.parseInt(trimmed, 10);
  const ref = parseSourceChannelRef(trimmed);
  // Для walk «число из ссылки» — это id сообщения, а не темы: одна и та же
  // форма URL, разный смысл, поэтому режим выбирается полем, а не адресом.
  return ref && ref.topicId > 0 ? ref.topicId : null;
}

/**
 * Стартовое сообщение id-walk: ссылка на сообщение либо голый id.
 *
 * Нормализация висит на ПОЛЕ, а не на объекте схемы: объектный `z.preprocess`
 * превратил бы `SourceChannelUpdateInput` из ZodObject в ZodPipe и отобрал у него
 * `.loose()`, которым edit-форма ослабляет схему под полный DTO.
 * Пустое поле формы приходит как `''` — его надо свести к `undefined`, иначе
 * `z.coerce` даст `0` и уронит валидацию на `min(1)` вместо «поле не заполнено».
 */
const walkFromMessageIdField = z.preprocess((raw) => {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'string' && raw.trim() === '') return undefined;
  return parseTmeMessageId(raw) ?? raw;
}, z.coerce.number().int().min(1).optional());

export const SourceChannelCreateInput = z.preprocess(
  (v) => {
    // Ссылку раскладываем в (slug, topicId) ДО валидации, чтобы админ мог
    // просто вставить URL темы, не выковыривая из неё числа руками.
    if (typeof v !== 'object' || v === null) return v;
    const obj = v as Record<string, unknown>;
    if (typeof obj.slug !== 'string') return obj;
    const ref = parseSourceChannelRef(obj.slug);
    if (!ref) return obj;
    return { ...obj, slug: ref.slug, topicId: obj.topicId ?? ref.topicId };
  },
  z
    .object({
      slug: channelSlug,
      /** Тема (топик) супергруппы-форума; 0 — обычный канал целиком. */
      topicId: z.coerce.number().int().min(0).default(0),
      /**
       * Плоская супергруппа: с какого сообщения начинать перебор id.
       * Указанное сообщение тоже попадёт в импорт.
       */
      walkFromMessageId: walkFromMessageIdField,
      // Опционально: если не передан, сервер вытащит название с t.me (с fallback на @slug).
      title: z.string().min(1).max(120).trim().optional(),
      cityId: z.uuid(),
      isActive: z.boolean().default(true),
      fetchCadenceMin: z.coerce.number().int().min(5).max(1440).default(15),
      kind: SourceChannelKind.default('EVENTS'),
    })
    .superRefine((val, ctx) => {
      if (val.walkFromMessageId !== undefined && val.topicId > 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['walkFromMessageId'],
          message: 'walkFromMessageId is incompatible with topicId: pick one way to read the source',
        });
      }
    }),
);
export type SourceChannelCreateInput = z.infer<typeof SourceChannelCreateInput>;

// Slug is immutable — not present in update input. Strict mode strips unknown keys silently.
export const SourceChannelUpdateInput = z
  .object({
    title: z.string().min(1).max(120).trim().optional(),
    cityId: z.uuid().optional(),
    isActive: z.boolean().optional(),
    fetchCadenceMin: z.coerce.number().int().min(5).max(1440).optional(),
    kind: SourceChannelKind.optional(),
    /**
     * Переставить курсор id-walk. Курсор застревает штатно (дыра шире лестницы
     * пробойника, ошибочный стартовый id), и без этого поля лечение сводилось бы
     * к пересозданию канала с потерей истории SourceMessage.
     */
    walkFromMessageId: walkFromMessageIdField,
  })
  .strip();
export type SourceChannelUpdateInput = z.infer<typeof SourceChannelUpdateInput>;

export const SourceChannelDto = z.object({
  id: z.uuid(),
  slug: z.string(),
  /** Тема (топик) супергруппы-форума; 0 — обычный канал. */
  topicId: z.number().int(),
  /** Курсор режима id-walk; null — режим выключен. */
  walkCursor: z.number().int().nullable(),
  title: z.string(),
  cityId: z.uuid(),
  isActive: z.boolean(),
  fetchCadenceMin: z.number().int(),
  kind: SourceChannelKind,
  lastFetchedAt: z.iso.datetime().nullable(),
  lastError: z.string().nullable(),
  consecutiveErrors: z.number().int(),
  messagesCount: z.number().int(),
  createdAt: z.iso.datetime(),
});
export type SourceChannelDto = z.infer<typeof SourceChannelDto>;
